import {
  Avatar,
  Button,
  HStack,
  SimpleGrid,
  SkeletonCircle,
  SkeletonText,
  Stack,
  StackProps,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import {
  RiProgress3Line,
  RiShareLine,
  RiShoppingBag2Line,
  RiWallet2Line,
} from "@remixicon/react";
import { lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardResellerInterface } from "../../constants/DashboardReseller";
import { useDarkWhiteColor } from "../../themes/colors";
import formatNumber from "../../utils/helperFunction";
import { ButtonCopyUrl } from "../button/ButtonCopyUrl";
import { CardInfo } from "../card/CardInfo";
import { CardSkeleton } from "../skeleton/CardSkeleton";

const WithdrawModal = lazy(() => import("../withdraw_page/WithdrawModal"));

interface Props extends StackProps {
  data: DashboardResellerInterface | undefined;
  loaded: boolean;
}

export const ProfileResellerStatistic = ({ data, loaded, ...rest }: Props) => {
  const navigate = useNavigate();
  const bgBnw = useDarkWhiteColor();

  const gradientBg = useColorModeValue(
    "linear(to-b, red.200 0%, transparent 100%)", // light mode
    "linear(to-b, red.900 0%, transparent 100%)" // dark mode
  );

  return (
    <Stack
      w={"100%"}
      flexDirection={["column", "column", "column", "row", "row"]}
      gap={4}
      {...rest}
    >
      <VStack
        p={4}
        bg={bgBnw}
        gap={4}
        borderRadius={"md"}
        align={"stretch"}
        flex={1}
      >
        <HStack w={"100%"}>
          {!loaded ? (
            <SkeletonCircle size={"10"} />
          ) : (
            <Avatar
              size={"lg"}
              name={data?.reseller.name || ""}
              src={
                data?.reseller.picture
                  ? `${process.env.REACT_APP_MEDIA_URL}${data?.reseller.picture}`
                  : ""
              }
            />
          )}

          <VStack w={"100%"} align={"stretch"} gap={0}>
            <HStack w={"100%"} justify={"space-between"}>
              <SkeletonText isLoaded={loaded} noOfLines={1}>
                <Text
                  textStyle={"h-reg-title"}
                  noOfLines={1}
                  textOverflow={"ellipsis"}
                >
                  {data?.reseller.name || "Reseller BisnisLink"}
                </Text>
              </SkeletonText>

              {data?.reseller.prefix && (
                <ButtonCopyUrl
                  storeId={data?.reseller?.prefix}
                  btnText="Salin Link Toko"
                  variant={"ghost"}
                  isDisabled={loaded ? false : true}
                />
              )}
            </HStack>

            {data?.reseller.prefix && (
              <Text
                textStyle={"h-subtitle"}
                variant={"secondary"}
                noOfLines={1}
                textOverflow={"ellipsis"}
              >
                {`${process.env.REACT_APP_FRONTEND_URL}/reseller/${data.reseller.prefix}`}
              </Text>
            )}
          </VStack>
        </HStack>

        <VStack
          w={"100%"}
          p={4}
          bgGradient={gradientBg}
          borderRadius={"md"}
          align={"stretch"}
        >
          <VStack w={"100%"} p={4}>
            <Text textStyle={"h-subtitle"} fontWeight={"semibold"}>
              Total Saldo
            </Text>

            <Text fontSize={[40, null, 42]} fontWeight={"semibold"}>
              {data?.reseller && formatNumber(data?.reseller.ready_point)}
            </Text>
          </VStack>

          <HStack w={"100%"}>
            <Button
              variant={"ghost"}
              size={["xs", null, "sm"]}
              flex={1}
              onClick={() => {
                navigate("/reseller/withdraw");
              }}
            >
              Riwayat
            </Button>

            <Suspense fallback={<CardSkeleton row={1} height={40} />}>
              <WithdrawModal pageType={2} />
            </Suspense>
          </HStack>
        </VStack>
      </VStack>

      {data && (
        <SimpleGrid
          className="simple-grid"
          columns={2}
          w={"100%"}
          gap={2}
          flex={1}
        >
          <CardInfo
            title={"Total Link Dibuka"}
            value={formatNumber(data.analytic.totalOpenedLink)}
            icon={RiWallet2Line}
            color="green"
            loaded={loaded}
          />

          <CardInfo
            title={"Total Penjualan"}
            value={formatNumber(data.analytic.totalSold)}
            icon={RiProgress3Line}
            color="yellow"
            loaded={loaded}
          />

          <CardInfo
            title={"Saldo Progress"}
            value={formatNumber(data.reseller.on_progress_point)}
            icon={RiShoppingBag2Line}
            color="blue"
            loaded={loaded}
          />

          <CardInfo
            title={"Saldo Siap Diambil"}
            value={formatNumber(data.reseller.ready_point)}
            icon={RiShareLine}
            color="red"
            loaded={loaded}
          />
        </SimpleGrid>
      )}
    </Stack>
  );
};
